/**
 * Warning threshold of the CRA reporting deadlines (DEV-132): how many hours
 * before a deadline it counts as due soon — DUE_SOON events, CRA-Check and
 * the triage use it.
 */

import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchReportingSettings, updateReportingSettings } from '../../lib/api';
import type { ReportingSettings } from '../../lib/types';

export function ReportingSettingsCard(): ReactNode {
  const { t } = useLingui();
  const [settings, setSettings] = useState<ReportingSettings | null>(null);
  const [hours, setHours] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReportingSettings()
      .then((next) => {
        setSettings(next);
        setHours(String(next.deadline_warn_hours));
      })
      .catch((err: unknown) => setError(errorMessage(err)));
  }, []);

  const value = Number(hours);
  const valid = settings !== null && Number.isInteger(value)
    && value >= settings.min_warn_hours && value <= settings.max_warn_hours;
  const changed = settings !== null && value !== settings.deadline_warn_hours;
  const defaultHours = settings?.default_warn_hours;
  const minHours = settings?.min_warn_hours;
  const maxHours = settings?.max_warn_hours;

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!valid || !changed || saving) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const next = await updateReportingSettings(value);
      setSettings(next);
      setHours(String(next.deadline_warn_hours));
      const savedHours = next.deadline_warn_hours;
      setSaved(t`Warnschwelle gespeichert: ${savedHours} h vor Fristablauf.`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2><Trans>Meldefristen</Trans></h2>
      </div>
      <div className="card-body">
        {error !== null ? <Banner kind="error">{error}</Banner> : null}
        {saved !== null ? <Banner kind="ok">{saved}</Banner> : null}
        {settings === null && error === null ? <Spinner label={t`Wird geladen…`} /> : null}
        {settings !== null ? (
          <form onSubmit={(event) => void save(event)}>
            <div className="field">
              <label htmlFor="deadline-warn-hours"><Trans>Warnschwelle (Stunden vor Fristablauf)</Trans></label>
              <input
                id="deadline-warn-hours"
                type="number"
                min={settings.min_warn_hours}
                max={settings.max_warn_hours}
                step={1}
                value={hours}
                onChange={(event) => {
                  setSaved(null);
                  setHours(event.target.value);
                }}
                aria-invalid={!valid}
                aria-describedby="deadline-warn-hours-hint"
                required
              />
              <span id="deadline-warn-hours-hint" className={valid ? 'hint' : 'err'}>
                {valid
                  ? t`Ab dann gilt eine Frist der Behördenmeldung als „läuft bald ab“: Fristereignis, Hinweis im CRA-Check und in der Triage. Standard: ${defaultHours} h.`
                  : t`Ganze Stunden zwischen ${minHours} und ${maxHours}.`}
              </span>
            </div>
            <div className="ops-dt-actions">
              <span />
              <button type="submit" className="primary" disabled={!valid || !changed || saving}>
                {saving ? <Spinner variant="on-accent" label={t`Wird gespeichert…`} /> : t`Speichern`}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}

export default ReportingSettingsCard;
